import { createSelector } from '@reduxjs/toolkit';

import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import type { JsonExchange } from '@skynet/data-shared-exchanges';

import type { AutoCompleteProps } from 'antd';
import type { JsonProjectAdmin, RetainersAdminPanelState } from './admin-panel/slice';
import type { RetainersState } from './slice';
import type { RetainersProjectsState } from './projects/slice';

import { filterAndSort, setDefaultName } from 'src/store/utils';
import { sortBy } from 'es-toolkit';
import type { JsonProject } from 'src/store/retainers/projects/types';
import type { RetainerTermsState } from './terms/types';

export interface Store {
  retainers: {
    index: RetainersState;
    projects: RetainersProjectsState;
    adminPanel: RetainersAdminPanelState;
    terms: RetainerTermsState;
  };
}

export const selectError = (store: Store) => {
  return (
    store.retainers.index.error ??
    store?.retainers?.projects?.error ??
    store?.retainers?.adminPanel?.error ??
    store?.retainers?.terms?.error
  );
};

export const selectIsListenSent = (store: Store) =>
  store.retainers.index.listenStatus === 'success';

const selectProjectList = (store: Store) => store.retainers.projects.list;

export const selectProjects = createSelector(
  selectProjectList,
  (items) => setDefaultName(items) ?? [],
);

export const selectAccountsAdmin = (store: Store) => store.retainers.adminPanel.userAccounts;

const selectProjectsAdminList = (store: Store) => store.retainers.adminPanel.projects;

export const selectProjectsAdmin = createSelector(selectProjectsAdminList, setDefaultName);

export const selectProjectsAdminLoading = (store: Store) =>
  store.retainers.adminPanel.requestStatus === 'pending';
export const selectIsAdminPanelUninitialized = (store: Store) =>
  store.retainers.adminPanel.requestStatus === null;
export const selectIsSidebarOpen = (store: Store) => store.retainers.index.isSidebarOpen;
export const selectTargetProject = (store: Store) => store.retainers.index.targetProject;
export const selectTargetExchange = (store: Store) => store.retainers.index.targetExchange;
export const selectIsRetainersExchangeLoading = (store: Store) =>
  store.retainers.index.listenStatus === 'pending';
export const selectOverviewActiveTab = (store: Store) => store.retainers.index.overviewActiveTab;

export const selectSortKey = (state: Store) => state.retainers.projects.sortKey;
export const selectSearchInput = (state: Store) => state.retainers.projects.searchInput;
export const selectHideEmptyAccounts = (state: Store) => state.retainers.projects.hideEmptyAccounts;

export const selectCurrentProject = createSelector(
  selectTargetProject,
  selectProjects,
  (targetProject: string, items: JsonProject[] | null) => {
    if (items === null || !targetProject) {
      return undefined;
    }

    return items.find((item) => item.id === +targetProject) ?? null;
  },
);

export const getDefaultProjectFromList = <T extends { id: number }>(items: T[] | null): T | null =>
  sortBy(items ?? [], ['id'])[0] ?? null;

export const selectDefaultProject = createSelector(selectProjects, (projects = []) =>
  getDefaultProjectFromList(projects),
);

export const selectDefaultAdminProject = createSelector(
  selectProjectsAdmin,
  getDefaultProjectFromList,
);

export const selectCurrentExchange = createSelector(
  selectTargetExchange,
  selectExchanges,
  (targetExchange: string, items: JsonExchange[]) => {
    const currentExchange: JsonExchange | undefined = targetExchange
      ? (items.find((item) => item.id === +targetExchange) ?? undefined)
      : undefined;
    return currentExchange;
  },
);

export const selectNextProject = createSelector(
  selectTargetProject,
  selectProjects,
  selectSortKey,
  (targetProject: string, items: JsonProject[] | null, sortKey: string) => {
    if (!items || !targetProject) return undefined;

    const sortedItems = filterAndSort(items, sortKey);
    const currentIndex = sortedItems.findIndex((item) => item.id === +targetProject);
    const nextIdx = currentIndex + 1;
    return nextIdx < sortedItems.length ? sortedItems[nextIdx] : undefined;
  },
);

export const selectPrevProject = createSelector(
  selectTargetProject,
  selectProjects,
  selectSortKey,
  (targetProject: string, items: JsonProject[] | null, sortKey: string) => {
    if (!items || !targetProject) return undefined;

    const sortedItems = filterAndSort(items, sortKey);
    const currentIndex = sortedItems.findIndex((item) => item.id === +targetProject);
    const prevIdx = currentIndex - 1;
    return prevIdx >= 0 ? sortedItems[prevIdx] : undefined;
  },
);

export const selectSortedProjects = createSelector(
  [selectProjects, selectSortKey, selectSearchInput],
  filterAndSort,
);

export const selectIsVisibleSideBar = createSelector(
  selectProjectsAdmin,
  selectProjects,
  (adminItems, items) => (adminItems ?? items ?? [])?.length > 1,
);

export const selectRetainerCompanies = (state: Store) => state.retainers.projects.companies;

export const selectRetainerCompanyOptions = createSelector(
  selectRetainerCompanies,
  (items: string[]) => {
    const companyOptions: Required<AutoCompleteProps>['options'] = items.map((company: string) => ({
      key: company,
      value: company,
      label: company,
    }));

    return companyOptions;
  },
);

export const selectIsNewProjectModalOpen = (store: Store) =>
  store.retainers.index.isNewProjectModalOpen;

const parseAccountName = (name: string) => {
  const cleanedName = name.replace('➕ ', '');
  // A name without the separator has no company part; default it so `company` stays a string
  const [email, company = ''] = cleanedName.split('<br/><b>');
  return {
    email,
    company: company.replace('</b>', ''),
  };
};

export const selectCurrentAdminProject = createSelector(
  selectTargetProject,
  selectProjectsAdmin,
  (targetProject: string, items: JsonProjectAdmin[] | null) => {
    if (items === null || !targetProject) {
      return undefined;
    }

    return items.find((item) => item.id === +targetProject) ?? null;
  },
);

export const selectUsersPopulated = createSelector(
  [selectAccountsAdmin, selectCurrentAdminProject],
  (accounts, currentProject) => {
    if (!currentProject) {
      return {
        linkedUsersPopulated: [],
        unlinkedUsersPopulated: [],
      };
    }

    const linkedUsers = currentProject.users ?? [];
    const notLinkedUsers = accounts.filter((account) => !linkedUsers.includes(account.id));

    const linkedUsersPopulated = linkedUsers
      .map((accountId) => {
        const account = accounts.find((acc) => acc.id === accountId);
        if (!account) return null;
        return {
          id: accountId,
          details: parseAccountName(account.name),
        };
      })
      .filter((account) => account !== null);

    const unlinkedUsersPopulated = notLinkedUsers
      .map((account) => ({
        id: account.id,
        details: parseAccountName(account.name),
      }))
      .filter((account) => account !== null);

    return {
      linkedUsersPopulated,
      unlinkedUsersPopulated,
      currentProject,
    };
  },
);

export const selectBindAccountError = (store: Store) =>
  store.retainers.adminPanel.resBindAccountErrorMessage;

export const selectBindAccountStatus = (store: Store) => store.retainers.adminPanel.resBindAccount;
export const selectUndinbAccountStatus = (store: Store) =>
  store.retainers.adminPanel.resUnbindAccount;

export const selectIsProjectExist = (projectId?: string) =>
  createSelector(selectProjects, selectProjectsAdmin, (projects, adminProjects) => {
    const list = projects?.length ? projects : adminProjects;
    if (list === null || projectId === undefined) {
      return undefined;
    }
    return list.some((item) => item.id === Number(projectId));
  });

export const selectResHideProjectStatus = (store: Store) => store.retainers.index.resHideProject;

export const selectProjectIdAfterHide = createSelector(
  selectNextProject,
  selectDefaultProject,
  (nextProject, defaultProject) => {
    return nextProject?.id ?? defaultProject?.id;
  },
);
